import React from 'react';
import { Printer } from 'lucide-react';
import TableActionsToolbar from './TableActionsToolbar';
import TabNavigationBar, { TabConfig } from './TabNavigationBar';
import WarehouseSelect from '../select/WarehouseSelect';
import { SpreadsheetAppearance, ENTERPRISE_TOOLBAR_CLS } from './spreadsheetAppearance';

interface DocumentTableToolbarProps<T extends string = string> {
  searchInput: string;
  setSearchInput: (value: string) => void;
  isSearchOpen: boolean;
  setIsSearchOpen: (value: boolean) => void;
  handleSearch: () => void;
  clearSearch: () => void;
  searchPlaceholder?: string;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  handleExcelUpload?: (file: File) => void;
  handleDownloadExcel?: () => void;
  triggerFileUpload?: () => void;
  showPlusButton?: boolean;
  onPlusClick?: () => void;
  showExcelButtons?: boolean;
  leftContent?: React.ReactNode;
  customUploadButton?: React.ReactNode;
  tabs?: TabConfig[];
  activeTab?: T;
  setActiveTab?: (tab: T) => void;
  // Warehouse dropdown props
  showWarehouse?: boolean;
  warehouseId?: string;
  onWarehouseChange?: (id: string) => void;
  warehouseDisabled?: boolean;
  // Save button props
  showSaveButton?: boolean;
  onSave?: () => void;
  isDirty?: boolean;
  isSaving?: boolean;
  /** Override the i18n key for the Save button tooltip when dirty. Defaults to 'common.saveChanges'. */
  saveTooltipKey?: string;
  /** When false, Save and Plus are hidden. Default true. */
  canWrite?: boolean;
  /** Optional printer icon button — navigates to a control panel */
  onPrintPdf?: () => void;
  searchAlwaysVisible?: boolean;
  /** When false, the search icon and box are hidden. Default true. */
  showSearch?: boolean;
  onCopyWarehouseQuantities?: () => void;
  canCopyWarehouseQuantities?: boolean;
  /** "enterprise" = card title bar (И&Р codebooks). Default "default" — current look unchanged. */
  appearance?: SpreadsheetAppearance;
  /** Opt-in neutral planning controls; other enterprise toolbars retain their title bar. */
  neutralControls?: boolean;
}

/**
 * Generic document table toolbar component
 * Reusable across Partner Pricing, Material Documents, Non-Material Documents, Work Orders, and Planning
 * Combines TableActionsToolbar (left) with optional TabNavigationBar (right)
 *
 * Usage examples:
 * - Partner Pricing: actions + tabs (Active, Classification, Archive)
 * - Material Documents: showWarehouse=true + actions, no tabs
 * - Non-Material Documents: actions only, no tabs
 * - Work Orders: actions only, no tabs
 * - Planning: actions + tabs (Buyers, Suppliers)
 */
function DocumentTableToolbar<T extends string = string>({
  searchInput,
  setSearchInput,
  isSearchOpen,
  setIsSearchOpen,
  handleSearch,
  clearSearch,
  searchPlaceholder,
  fileInputRef,
  handleExcelUpload,
  handleDownloadExcel,
  triggerFileUpload,
  showPlusButton = true,
  onPlusClick,
  showExcelButtons = true,
  leftContent,
  customUploadButton,
  tabs,
  activeTab,
  setActiveTab,
  showWarehouse = false,
  warehouseId,
  onWarehouseChange,
  warehouseDisabled = false,
  showSaveButton = false,
  onSave,
  isDirty = false,
  isSaving = false,
  saveTooltipKey,
  canWrite = true,
  onPrintPdf,
  searchAlwaysVisible = false,
  showSearch = true,
  onCopyWarehouseQuantities,
  canCopyWarehouseQuantities = false,
  appearance = 'default',
  neutralControls = false,
}: DocumentTableToolbarProps<T>) {
  // Render warehouse dropdown if showWarehouse is true
  const warehouseContent = showWarehouse ? (
    <div className={`w-72 ${warehouseDisabled ? 'opacity-60' : ''}`}>
      <WarehouseSelect
        value={warehouseId || ''}
        disabled={warehouseDisabled}
        required={false}
        onChange={(id) => onWarehouseChange?.(id || '')}
      />
    </div>
  ) : null;

  // Combine warehouse with any custom leftContent
  const combinedLeftContent = (
    <>
      {warehouseContent}
      {leftContent}
    </>
  );

  return (
    <div
      className={appearance === 'enterprise' ? ENTERPRISE_TOOLBAR_CLS : "p-2 flex items-center justify-between"}
      style={neutralControls
        ? { background: '#f9fafb' }
        : { background: `linear-gradient(to right, var(--color-primary), var(--color-secondary))` }}
    >
      <TableActionsToolbar
        neutralControls={neutralControls}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        isSearchOpen={isSearchOpen}
        setIsSearchOpen={setIsSearchOpen}
        handleSearch={handleSearch}
        clearSearch={clearSearch}
        searchPlaceholder={searchPlaceholder}
        fileInputRef={fileInputRef}
        handleExcelUpload={handleExcelUpload}
        handleDownloadExcel={handleDownloadExcel}
        triggerFileUpload={triggerFileUpload}
        showPlusButton={showPlusButton}
        onPlusClick={onPlusClick}
        showExcelButtons={showExcelButtons}
        leftContent={combinedLeftContent}
        customUploadButton={customUploadButton}
        showSaveButton={showSaveButton}
        onSave={onSave}
        isDirty={isDirty}
        isSaving={isSaving}
        saveTooltipKey={saveTooltipKey}
        canWrite={canWrite}
        searchAlwaysVisible={searchAlwaysVisible}
        showSearch={showSearch}
        onCopyWarehouseQuantities={onCopyWarehouseQuantities}
        canCopyWarehouseQuantities={canCopyWarehouseQuantities}
      />
      {tabs && activeTab && setActiveTab && (
        <TabNavigationBar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={tabs}
        />
      )}
      {onPrintPdf && (
        <button
          onClick={onPrintPdf}
          className="group relative flex items-center justify-center p-1.5 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 hover:opacity-90 focus:outline-none focus:ring-2 bg-white"
          style={{ border: '2px solid var(--color-primary)' }}
          title="Print"
        >
          <Printer size={14} strokeWidth={2.5} style={{ color: 'var(--color-primary)' }} />
        </button>
      )}
    </div>
  );
}

export default DocumentTableToolbar;
